/**
 * 4-screen onboarding: welcome → language → profile → rules acceptance.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { updateMe } from '../lib/api';
import { useSession } from '../lib/session';
import { useRouter } from '../lib/router';
import { LOCALE_LABELS, SUPPORTED_LOCALES, useT, type SupportedLocale } from '../lib/i18n';
import { MainButton } from '../lib/MainButton';
import { BackButton } from '../lib/BackButton';
import { BrandMark } from '../components/Brand';
import { TextInput, TextArea } from '../components/TextInput';
import { hapticError, hapticSelection, hapticSuccess } from '../lib/telegram';

type Step = 0 | 1 | 2 | 3 | 4;

const STEPS: readonly Step[] = [0, 1, 2, 3, 4] as const;
const PHONE_RE = /^\+\d{6,15}$/;

export function OnboardingPage(): JSX.Element {
  const { me, refresh } = useSession();
  const { navigate } = useRouter();
  const { t, locale, setLocale } = useT();
  const [step, setStep] = useState<Step>(0);
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [isDriver, setIsDriver] = useState(me?.isDriver ?? false);
  const [phone, setPhone] = useState(me?.phone ?? '');
  const [phoneVisible, setPhoneVisible] = useState(true);
  const [accepted, setAccepted] = useState(false);

  const phoneTrim = phone.replace(/\s+/g, '');
  const phoneValid = phoneTrim === '' || PHONE_RE.test(phoneTrim);

  const save = useMutation({
    mutationFn: async () => {
      await updateMe({
        displayName: displayName.trim(),
        bio: bio.trim() || undefined,
        isDriver,
        locale,
        phone: phoneTrim || '',
        phoneVisible: !!phoneTrim && phoneVisible,
        acceptRules: true,
      });
    },
    onSuccess: async () => {
      hapticSuccess();
      await refresh();
      navigate('/');
    },
  });

  const handleNext = (): void => {
    if (step === 0 || step === 1) setStep((step + 1) as Step);
    else if (step === 2) {
      if (displayName.trim().length < 1) {
        hapticError();
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!phoneValid) {
        hapticError();
        return;
      }
      setStep(4);
    } else if (accepted) {
      save.mutate();
    }
  };

  const ctaText =
    step < 4
      ? step === 3 && !phoneTrim
        ? t('onboarding.phone.skip')
        : t('onboarding.cta.continue')
      : accepted
        ? t('onboarding.cta.getStarted')
        : t('onboarding.cta.acceptToContinue');

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      {step > 0 && <BackButton onBack={() => setStep((step - 1) as Step)} />}

      <Dots step={step} />

      <div key={step} className="mt-6 flex-1 animate-step-in">
        {step === 0 && <WelcomeStep />}
        {step === 1 && (
          <LanguageStep
            current={locale}
            onChange={(l) => {
              if (l !== locale) hapticSelection();
              void setLocale(l, false);
            }}
          />
        )}
        {step === 2 && (
          <ProfileStep
            displayName={displayName}
            setDisplayName={setDisplayName}
            bio={bio}
            setBio={setBio}
            isDriver={isDriver}
            setIsDriver={setIsDriver}
          />
        )}
        {step === 3 && (
          <PhoneStep
            phone={phone}
            setPhone={setPhone}
            phoneVisible={phoneVisible}
            setPhoneVisible={setPhoneVisible}
            valid={phoneValid}
          />
        )}
        {step === 4 && <RulesStep accepted={accepted} setAccepted={setAccepted} />}
      </div>

      <MainButton
        text={ctaText}
        onClick={handleNext}
        loading={save.isPending}
        disabled={
          (step === 2 && displayName.trim().length < 1) ||
          (step === 3 && !phoneValid) ||
          (step === 4 && !accepted)
        }
      />
    </div>
  );
}

function Dots({ step }: { step: Step }): JSX.Element {
  return (
    <div className="mb-2 flex items-center justify-center gap-2">
      {STEPS.map((i) => (
        <span
          key={i}
          className={
            'h-1.5 rounded-pill transition-[width,background-color] duration-200 ease-out ' +
            (i === step ? 'w-8 bg-tg-text' : 'w-2 bg-[var(--border-medium)]')
          }
        />
      ))}
    </div>
  );
}

function WelcomeStep(): JSX.Element {
  const { t } = useT();
  return (
    <section className="space-y-6 text-center">
      <div className="flex justify-center">
        <BrandMark size={80} />
      </div>
      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-tg-text">{t('onboarding.welcome.title')}</h1>
        <p className="text-sm text-tg-hint">{t('onboarding.welcome.tagline')}</p>
        <a
          href="https://triardor.com/en/"
          target="_blank"
          rel="noreferrer"
          className="inline-block text-xs text-tg-hint underline"
        >
          {t('common.madeByTriardor')}
        </a>
      </div>
      <ul className="space-y-3 text-left">
        <Bullet title={t('onboarding.welcome.b1.title')} body={t('onboarding.welcome.b1.body')} />
        <Bullet title={t('onboarding.welcome.b2.title')} body={t('onboarding.welcome.b2.body')} />
        <Bullet title={t('onboarding.welcome.b3.title')} body={t('onboarding.welcome.b3.body')} />
        <Bullet title={t('onboarding.welcome.b4.title')} body={t('onboarding.welcome.b4.body')} />
      </ul>
    </section>
  );
}

function Bullet({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <li className="flex gap-3 rounded-card bg-tg-bg-secondary p-3">
      <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
        {/* Brand orbit + arrow mark (echoes the Waylo icon). */}
        <svg
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M20.5 12a8.5 8.5 0 1 1-4-7.2" />
          <path d="M21 3v5M21 3h-5" />
        </svg>
      </div>
      <div>
        <p className="font-semibold text-tg-text">{title}</p>
        <p className="text-sm text-tg-hint">{body}</p>
      </div>
    </li>
  );
}

function LanguageStep({
  current,
  onChange,
}: {
  current: SupportedLocale;
  onChange: (l: SupportedLocale) => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold text-tg-text">{t('onboarding.language.title')}</h2>
        <p className="text-sm text-tg-hint">{t('onboarding.language.hint')}</p>
      </header>
      <div className="space-y-2">
        {SUPPORTED_LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            className={
              'flex w-full items-center justify-between rounded-card p-4 text-left transition ' +
              (current === l
                ? 'border border-transparent bg-[var(--border-medium)]'
                : 'border border-[var(--border-light)] bg-tg-bg-secondary')
            }
          >
            <span className="font-medium text-tg-text">{LOCALE_LABELS[l]}</span>
            {current === l && <span className="text-brand-green">✓</span>}
          </button>
        ))}
      </div>
    </section>
  );
}

function PhoneStep({
  phone,
  setPhone,
  phoneVisible,
  setPhoneVisible,
  valid,
}: {
  phone: string;
  setPhone: (v: string) => void;
  phoneVisible: boolean;
  setPhoneVisible: (v: boolean) => void;
  valid: boolean;
}): JSX.Element {
  const { t } = useT();
  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold text-tg-text">{t('onboarding.phone.title')}</h2>
        <p className="text-sm text-tg-hint">{t('onboarding.phone.hint')}</p>
      </header>
      <TextInput
        id="phone"
        label={t('phone.label')}
        type="tel"
        inputMode="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t('phone.placeholder')}
        error={!valid ? t('phone.invalid') : undefined}
      />
      {phone.trim() !== '' && valid && (
        <label className="flex items-center justify-between rounded-card bg-tg-bg-secondary p-3">
          <span className="text-sm text-tg-text">{t('privacy.sharePhone')}</span>
          <input
            type="checkbox"
            checked={phoneVisible}
            onChange={(e) => setPhoneVisible(e.target.checked)}
            className="h-5 w-5 accent-brand-green"
          />
        </label>
      )}
    </section>
  );
}

function ProfileStep({
  displayName,
  setDisplayName,
  bio,
  setBio,
  isDriver,
  setIsDriver,
}: {
  displayName: string;
  setDisplayName: (v: string) => void;
  bio: string;
  setBio: (v: string) => void;
  isDriver: boolean;
  setIsDriver: (v: boolean) => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-xl font-bold text-tg-text">{t('onboarding.profile.title')}</h2>
        <p className="text-sm text-tg-hint">{t('onboarding.profile.hint')}</p>
      </header>
      <TextInput
        id="displayName"
        label={t('onboarding.profile.displayName')}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        maxLength={60}
        autoFocus
      />
      <TextArea
        id="bio"
        label={t('onboarding.profile.bio')}
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        maxLength={280}
        placeholder={t('onboarding.profile.bioPh')}
      />
      <label className="flex items-center justify-between rounded-card bg-tg-bg-secondary p-4">
        <span>
          <p className="font-medium text-tg-text">{t('onboarding.profile.driver')}</p>
          <p className="text-sm text-tg-hint">{t('onboarding.profile.driverHint')}</p>
        </span>
        <input
          type="checkbox"
          checked={isDriver}
          onChange={(e) => {
            hapticSelection();
            setIsDriver(e.target.checked);
          }}
          className="h-6 w-6 accent-brand-green"
        />
      </label>
    </section>
  );
}

function RulesStep({
  accepted,
  setAccepted,
}: {
  accepted: boolean;
  setAccepted: (v: boolean) => void;
}): JSX.Element {
  const { t } = useT();
  const acceptParts = t('onboarding.rules.accept').split('{termsLink}');
  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-xl font-bold text-tg-text">{t('onboarding.rules.title')}</h2>
        <p className="text-sm text-tg-hint">{t('onboarding.rules.hint')}</p>
      </header>
      <ol className="list-decimal space-y-3 pl-5 text-sm text-tg-text">
        {(['r1', 'r2', 'r3', 'r4', 'r5'] as const).map((r) => (
          <li key={r}>
            <strong>{t(`onboarding.rules.${r}.t` as const)}</strong>{' '}
            {t(`onboarding.rules.${r}.b` as const)}
          </li>
        ))}
      </ol>
      <label className="flex items-start gap-3 rounded-card bg-tg-bg-secondary p-4">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="mt-1 h-5 w-5 accent-brand-green"
        />
        <span className="text-sm text-tg-text">
          {acceptParts[0]}
          <a href="#/terms" className="underline">
            {t('onboarding.rules.terms')}
          </a>
          {acceptParts[1] ?? ''}
        </span>
      </label>
    </section>
  );
}
